import i18n from "i18next";

import {HeatmapConfigUi} from "./HeatmapConfigUi";
import {Button} from "../../gui/controls/Button";
import {HeatmapIcon, PulseIcon} from "./icons";
import {SubMenu} from "../../gui/controls/SubMenu";
import {TOOLBAR} from "../../gui/GuiViewerToolbarConst";
import * as dte from "../DtEventTypes";
import { StreamHeatmap } from "./StreamHeatmap";

function setButtonDisabled(btn, isDisabled) {
	if (isDisabled) {
		btn.setState(Button.State.DISABLED);
		return;
	}
	if (btn.getState() === Button.State.DISABLED) {
		btn.setState(Button.State.INACTIVE);
	}
}

/** View for the stream data visualization controls (heatmap). */
export class DataVizControls {
	constructor(streamMgr, eventSource) {
		this.eventSource = eventSource;
		this.heatmap = new StreamHeatmap(streamMgr, eventSource);
		this.streamMgr = streamMgr;

		this.heatmapButton = this.createHeatmapButton();

		this.toolbarButton = this.createMenu([this.heatmapButton]);

		this.boundOnHeatmapChanged = this.onHeatmapChanged.bind(this);
		this.boundOnStreamsInfoChanged = this.onStreamsInfoChanged.bind(this);
	}

	init(viewer) {
		this.viewer = viewer;

		this.heatmap.init(this.viewer);

		this.hasToolbar = Autodesk.Viewing.GuiViewer3D && viewer instanceof Autodesk.Viewing.GuiViewer3D;

		if (this.hasToolbar) {
			this.viewer.waitForToolbar()
				.then(() => this.addToolbarButton())
				.then(() => this.updateButtonState());
		}
		this.heatmapCfgUi = new HeatmapConfigUi(viewer, this, this.heatmap, this.streamMgr);
		this.eventSource.addEventListener(dte.DT_HEATMAP_CHANGED_EVENT, this.boundOnHeatmapChanged);
		this.eventSource.addEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnStreamsInfoChanged);
	}

	dispose() {
		if (this.hasToolbar) {
			this.viewer.getToolbar()?.getControl(TOOLBAR.MODELTOOLSID).removeControl(this.toolbarButton);
		}
		this.heatmapCfgUi?.dispose();
		this.heatmap.dispose();

		this.eventSource.removeEventListener(dte.DT_HEATMAP_CHANGED_EVENT, this.boundOnHeatmapChanged);
		this.eventSource.removeEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnStreamsInfoChanged);
	}

	/**
	 * Toggles the disabled status of the parent control
	 * @param isDisabled
	 */
	setDisabled(isDisabled) {
		if (this.hasToolbar) {
			setButtonDisabled(this.heatmapButton, isDisabled);
		}
	}

	/**
	 * Sets the visibility of the heatmap widget.
	 * @param isVisible
	 */
	setHeatmapVisibility(isVisible) {
		const prevVisible = this.getHeatmapVisibility();

		if (!this.hasToolbar) {
			this.heatmapCfgUi.setVisible(isVisible);
		} else if (this.heatmapButton.getState() !== Button.State.DISABLED) {
			this.heatmapCfgUi.setVisible(isVisible);
			this.heatmapButton.setState(isVisible ? Button.State.ACTIVE : Button.State.INACTIVE);
		}

		const nowVisible = this.getHeatmapVisibility();

		if (prevVisible === nowVisible) {
			return;
		}
		this.eventSource.dispatchEvent({
			type: dte.DT_DATA_VISIBILITY_CHANGED_EVENT,
			change: { isHeatmapVisible: nowVisible },
		});
	}

	/**
	 * @returns {boolean} heatmap visible
	 */
	getHeatmapVisibility() {
		if (!this.hasToolbar) {
			return this.heatmapCfgUi.isVisible();
		}

		if (this.toolbarButton.getState() !== Button.State.ACTIVE) {
			return false;
		}
		return this.heatmapButton.getState() === Button.State.ACTIVE;
	}

	/**
	 * Adds itself to the toolbar
	 * @private
	 */
	addToolbarButton() {
		const wasAdded = this.viewer.getToolbar()
			.getControl(TOOLBAR.MODELTOOLSID)
			.addControl(this.toolbarButton);

		console.assert(wasAdded, 'Unable to add stream visualization toolbar control');
	}

	/**
	 * Creates the heatmap button control.
	 * @returns {Autodesk.Viewing.UI.Button}
	 * @private
	 */
	createHeatmapButton() {
		const button = new Button('toolbar-heatmap');
		button.icon.innerHTML = HeatmapIcon;
		button.setIcon('icon-heatmap');
		button.setState(Button.State.DISABLED);
		button.setToolTip(i18n.t('Heatmap'));
		button.onClick = () => {
			this.setHeatmapVisibility(button.getState() === Button.State.INACTIVE);
		}
		return button;
	}

	/**
	 * Creates the sub-menu (fly-out) control, and adds sub controls.
	 * @param controls Controls to be present in the sub-menu (in-order)
	 * @returns {Autodesk.Viewing.UI.SubMenu}
	 * @private
	 */
	createMenu(controls) {
		const menu = new SubMenu('toolbar-dataviz');
		menu.icon.innerHTML = PulseIcon;
		menu.setIcon('icon-dataviz');
		menu.setState(Button.State.DISABLED);
		menu.setToolTip(i18n.t('Stream Data Visualization'));

		controls.forEach((control) => menu.addControl(control));

		return menu;
	}

	/**
	 * On changes to the heatmap visibility or configuration.
	 * @param attrIdFilter The new stream attribute
	 * @param ctype Type of change
	 * @private
	 */
	onHeatmapChanged({ change: { attrIdFilter, ctype } }) {
		if (!this.heatmapCfgUi) return;

		switch (ctype) {
			case 'shading':
				return this.heatmapCfgUi.setActiveAttribute(attrIdFilter);
			case 'range':
				return this.heatmapCfgUi.updateScale(attrIdFilter);
		}
	}

	/**
	 * Stream info changed.
	 * @private
	 */
	onStreamsInfoChanged() {
		this.updateButtonState();
	}

	/**
	 * Sets enabled-ness of controls based on the presence of displayable stream and stream attributes
	 * @returns {Promise<void>}
	 * @private
	 */
	async updateButtonState() {
		const streamInfos = await this.streamMgr.getAllStreamInfos();
		const hasStreamAttrs = streamInfos.flatMap(s => s.streamAttrs).length > 0;
		if (!hasStreamAttrs) await this.heatmap.show(null);

		// Update heatmap Cfg UI from here instead of registering it for events.
		this.heatmapCfgUi?.setStreamInfos(streamInfos);

		if (this.hasToolbar) {
			setButtonDisabled(this.heatmapButton, !hasStreamAttrs);
		}
	}
}
